import { Elevion } from 'lib-shared-elevion';
import {
  elevionCustomAttributePrefix,
  elevionSystemAttributePrefix,
  ElevionSystemAttributePrefix,
  ElevionSystemAttributeValue,
} from 'lib-shared-types-api';
import { useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

import { isCustomFieldValueEmpty } from '../isCustomFieldValueEmpty';
import { CustomField } from '../useLazyCustomFields/useLazyCustomFields.types';

type StripSystemPrefix<T extends string> =
  T extends `${ElevionSystemAttributePrefix}${infer U}` ? U : never;

type ElevionSystemAttributeValueSimplified = {
  [K in StripSystemPrefix<
    keyof ElevionSystemAttributeValue
  >]: ElevionSystemAttributeValue[`${ElevionSystemAttributePrefix}${K}`]['value'];
};

export function evalCustomFieldsVisibility(
  customFields: readonly CustomField[],
  systemContext: ElevionSystemAttributeValueSimplified,
): Record<string, boolean> {
  const ctx = {};
  Object.keys(systemContext).forEach((k) => {
    ctx[`${elevionSystemAttributePrefix}${k}`] = systemContext[k]?.[0]
      ? systemContext[k]
      : [];
  });
  customFields.forEach(({ attribute, value }) => {
    ctx[`${elevionCustomAttributePrefix}${attribute.identifier}`] =
      isCustomFieldValueEmpty(value)
        ? attribute.data_type === 'string_list'
          ? []
          : ''
        : value?.value;
  });

  const result: Record<string, boolean> = {};
  try {
    customFields.forEach((c) => {
      result[c.attribute.id] = c.attribute.visibility_condition
        ? Elevion.evalJson(
            JSON.stringify(c.attribute.visibility_condition),
            ctx,
          )
        : true;
    });
  } catch {}

  return result;
}

/**
 * Checks if custom fields are visible based on their conditional visibility
 * status. It tracks the changes in the custom attributes and system context and
 * returns the new visibility status.
 *
 * @returns An object mapping custom field ids to their visibility status.
 */
export const useCustomFieldsVisibility = (
  customFields: readonly CustomField[],
  systemContext: ElevionSystemAttributeValueSimplified,
): {
  data: Record<string, boolean>;
} => {
  const [fieldsVisibility, setFieldsVisibility] = useState<
    Record<string, boolean>
  >({});

  useDeepCompareEffect(() => {
    if (customFields.length === 0) {
      return;
    }

    setFieldsVisibility(
      evalCustomFieldsVisibility(customFields, systemContext),
    );
  }, [customFields, systemContext]);

  return {
    data: fieldsVisibility,
  };
};
